import { Controller } from "@hotwired/stimulus"

// "Record in my voice" (shared/_voice_recorder) - on an Ask me answer and on
// a twin note alike: the owner says it into the mic, it uploads as its audio
// (ProfilePageAnswersController#recording, VideoNotesController#recording),
// and that recording - not the synthesized voice - plays while the avatar
// talks. "Use my twin's voice" hands it back. Nothing is recorded until
// Record is pressed, and nothing uploads until Stop.
const MAX_SECONDS = 90

export default class extends Controller {
  static targets = ["record", "recordLabel", "recordIcon", "play", "drop", "status"]
  static values = { url: String, audio: String, recorded: Boolean, recordedHint: String, droppedHint: String }

  disconnect() {
    this._stopTracks()
    clearInterval(this._timer)
  }

  async toggle(event) {
    event.preventDefault()
    if (this._recorder?.state === "recording") return this._recorder.stop()
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      return this._say("This browser can't record audio.")
    }
    try {
      this._stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (_error) {
      return this._say("Allow the microphone to record your answer.")
    }
    const type = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"].find((candidate) => MediaRecorder.isTypeSupported?.(candidate))
    this._recorder = new MediaRecorder(this._stream, type ? { mimeType: type } : undefined)
    this._chunks = []
    this._recorder.addEventListener("dataavailable", (e) => { if (e.data.size) this._chunks.push(e.data) })
    this._recorder.addEventListener("stop", () => this._upload())
    this._recorder.start()
    this._started = Date.now()
    this._setRecording(true)
    this._timer = setInterval(() => {
      const seconds = Math.round((Date.now() - this._started) / 1000)
      this._say(`Recording… ${seconds}s - press Stop when you're done.`)
      if (seconds >= MAX_SECONDS) this._recorder.stop()
    }, 500)
  }

  async _upload() {
    clearInterval(this._timer)
    this._setRecording(false)
    this._stopTracks()
    const type = (this._recorder.mimeType || "audio/webm").split(";")[0]
    const blob = new Blob(this._chunks, { type })
    if (blob.size < 1000) return this._say("That was too short - try again.")
    const data = new FormData()
    data.append("audio", blob, `recording.${type.includes("mp4") ? "m4a" : type.split("/")[1]}`)
    this.recordTarget.disabled = true
    this._say("Saving your recording…")
    try {
      const response = await fetch(this.urlValue, { method: "POST", body: data, headers: { Accept: "application/json", "X-CSRF-Token": this._token() } })
      const result = await response.json()
      if (!response.ok || !result.ok) throw new Error(result.error || "Couldn't save the recording.")
      this.audioValue = result.audio_url
      this.recordedValue = true
      this._sync()
      this._say(this.recordedHintValue || "Your own recording plays.")
    } catch (error) {
      this._say(error.message || "Couldn't save the recording. Try again.")
    } finally {
      this.recordTarget.disabled = false
    }
  }

  async drop(event) {
    event.preventDefault()
    try {
      const response = await fetch(this.urlValue, { method: "DELETE", headers: { Accept: "application/json", "X-CSRF-Token": this._token() } })
      if (!response.ok) throw new Error()
      this.recordedValue = false
      this.audioValue = ""
      this._sync()
      this._say(this.droppedHintValue || "Your twin will say it again - give it a moment.")
    } catch (_error) {
      this._say("Couldn't switch back. Try again.")
    }
  }

  play(event) {
    event.preventDefault()
    if (!this.audioValue) return
    this._player ||= new Audio()
    this._player.src = `${this.audioValue}?t=${Date.now()}`
    this._player.play().catch(() => this._say("Couldn't play it."))
  }

  _sync() {
    this.playTarget.hidden = !this.audioValue
    this.dropTarget.hidden = !this.recordedValue
    this.recordLabelTarget.textContent = this.recordedValue ? "Record again" : "Record in my voice"
  }

  _setRecording(on) {
    this.recordTarget.classList.toggle("is-recording", on)
    this.recordIconTarget.className = `bi ${on ? "bi-stop-fill" : "bi-mic"} me-1`
    this.recordLabelTarget.textContent = on ? "Stop" : (this.recordedValue ? "Record again" : "Record in my voice")
  }

  _stopTracks() {
    this._stream?.getTracks().forEach((track) => track.stop())
    this._stream = null
  }

  _say(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }

  _token() {
    return document.querySelector("meta[name=csrf-token]")?.content || ""
  }
}
